import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  clearPaymentLinkingError,
  initializePaymentSession,
} from "../../redux/slices/paymentLinkingSlice";
import {
  Avatar,
  CustomerDetails,
  DynamicMetadata,
  ErrorState,
  LoadingState,
  PaymentSummary,
  PrimaryButton,
} from "../components";

const PaymentInformation = () => {
  const { linkCode } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { paymentLink, error, loading } = useSelector((state) => ({
    paymentLink: state.paymentLinking.paymentLink,
    error: state.paymentLinking.error,
    loading:
      state.paymentLinking.loading.details ||
      state.paymentLinking.loading.initialize,
  }));

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    dispatch(initializePaymentSession({ linkCode }))
      .unwrap()
      .catch((err) => {
        console.error("Failed to initialize session:", err);
      });
  }, [linkCode, dispatch]);

  // Fast-path for gopay-only RECURRING_INITIAL links: skip the
  // payment-details splash + the method picker entirely and drop
  // the customer straight into the GoPay linking panel. The
  // shared session is already bootstrapped above so the
  // GoPayLinking page can immediately call /gopay/initiate.
  useEffect(() => {
    if (!paymentLink) return;

    const methods = paymentLink.payment_methods || [];
    if (methods.length === 0) return;

    const isGoPayOnly = methods.every(
      (m) => m.type === "ewallet" || (m.provider || "").toLowerCase() === "midtrans"
    );
    const isRecurring = paymentLink.settings?.is_recurring;

    if (isGoPayOnly && isRecurring) {
      navigate(`/b2b/linking/${linkCode}/gopay`, { replace: true });
    }
  }, [paymentLink, linkCode, navigate]);

  // Clear error when component unmounts or linkCode changes
  useEffect(() => {
    return () => {
      if (error) {
        dispatch(clearPaymentLinkingError());
      }
    };
  }, [linkCode, error, dispatch]);

  const handleContinue = () => {
    navigate(`/b2b/linking/${linkCode}/payment-method`);
  };

  if (!mounted || loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (!paymentLink) {
    return null;
  }

  const { merchant, customer, items, pricing, metadata } = paymentLink;

  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{ backgroundColor: "#F9FAFC" }}
    >
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Avatar
            name={merchant?.name}
            imageUrl={merchant?.logo_url}
            size="md"
          />
          <h1 className="text-lg sm:text-xl font-bold" style={{ color: "#3B3B3B" }}>
            {merchant?.name || "Lyrid.io"}
          </h1>
        </div>

        <div className="mb-6">
          <h2 className="text-xl sm:text-2xl font-bold mb-1" style={{ color: "#3B3B3B" }}>
            Payment Details
          </h2>
          <p className="text-xs sm:text-sm" style={{ color: "#515164" }}>
            Please review your payment details below and continue to payment.
          </p>
        </div>

        <CustomerDetails customer={customer} />

        <DynamicMetadata metadata={metadata} />

        <PaymentSummary items={items} pricing={pricing} />

        <PrimaryButton onClick={handleContinue}>
          Choose Payment Method
        </PrimaryButton>

        <div className="text-center">
          <p className="text-[10px] sm:text-xs" style={{ color: "#828282" }}>
            © 2025 {process.env.REACT_APP_NAME || "SingaEngine"}. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentInformation;
